/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import { AbstractRestService, arrayOf, identity, RestSettings } from '@gh/core-data';
import { Observable } from 'rxjs';
import { GraphicalTakeOffHousePlanSaveDto, GraphicalTakeOffHousePlanViewDto } from './dto';
import { GraphicalTakeOffHousePlanSaveMapper, GraphicalTakeOffHousePlanViewMapper } from './house-plan-view.mapper';
import { getGTOEndpoint } from './utils';

/**
 * House plan view REST service
 */
@Injectable()
export class GraphicalTakeOffHousePlanViewRestService extends AbstractRestService {
  /**
   * Constructor
   *
   * @param {RestSettings} settings - REST settings
   * @returns {void}
   */
  constructor(settings: RestSettings) {
    super(settings);
  }

  /**
   * Fetch all house plan views by house plan id
   *
   * @param {ID} hpId - House plan id
   * @param {string} entryPoint - Entry point
   * @returns {Observable<GraphicalTakeOffHousePlanViewDto[]>} - List of house plan views
   */
  findAll(hpId: ID, entryPoint: string): Observable<GraphicalTakeOffHousePlanViewDto[]> {
    return this.httpGet(
      getGTOEndpoint(`${hpId}/#-views?entryPoint=${entryPoint}`, entryPoint),
      arrayOf(GraphicalTakeOffHousePlanViewMapper),
    );
  }

  /**
   * Get house plan view by id
   *
   * @param {ID} hpId - House plan id
   * @param {ID} hpViewId - House plan view id
   * @param {string} entryPoint - Entry point
   * @param {boolean} useSnapToPoint - Use snap to point
   * @returns {Observable<GraphicalTakeOffHousePlanViewDto>} - House plan view
   */
  get(hpId: ID, hpViewId: ID, entryPoint: string, useSnapToPoint: boolean = true):
    Observable<GraphicalTakeOffHousePlanViewDto> {
    return this.httpGet(
      getGTOEndpoint(
        `${hpId}/#-views/${hpViewId}?entryPoint=${entryPoint}&useSnapToPoint=${useSnapToPoint}`,
        entryPoint,
      ),
      GraphicalTakeOffHousePlanViewMapper,
    );
  }

  /**
   * Create house plan view
   *
   * @param {ID} hpId - House plan id
   * @param {GraphicalTakeOffHousePlanViewDto} hpView - House plan view
   * @param {string} entryPoint - Entry point
   * @returns {Observable<GraphicalTakeOffHousePlanViewDto>} - New house plan view
   */
  create(hpId: ID, hpView: GraphicalTakeOffHousePlanViewDto, entryPoint: string):
    Observable<GraphicalTakeOffHousePlanViewDto> {
    return this.httpPost(
      getGTOEndpoint(`${hpId}/#-views?entryPoint=${entryPoint}`, entryPoint),
      hpView,
      GraphicalTakeOffHousePlanViewMapper,
    );
  }

  /**
   * Update house plan view
   *
   * @param {ID} hpId - House plan id
   * @param {ID} hpViewId - House plan view id
   * @param {any} data - Picture data
   * @param {string} entryPoint - Entry point
   * @param {boolean} useSnapToPoint - Use snap to point
   * @returns {Observable<GraphicalTakeOffHousePlanViewDto>} - New house plan view
   */
  update(hpId: ID, hpViewId: ID,
         data: GraphicalTakeOffHousePlanSaveDto,
         entryPoint: string,
         useSnapToPoint: boolean = true):
    Observable<GraphicalTakeOffHousePlanViewDto> {
    return this.httpPut(
      getGTOEndpoint(`${hpId}/#-views/${hpViewId}?entryPoint=${entryPoint}&useSnapToPoint=${useSnapToPoint}`,
                     entryPoint),
      data,
      GraphicalTakeOffHousePlanSaveMapper,
      GraphicalTakeOffHousePlanViewMapper,
    );
  }

  /**
   * Delete house plan view
   *
   * @param {ID} hpId - House plan id
   * @param {ID} hpViewId - House plan view id
   * @param {string} entryPoint - Entry point
   * @returns {Observable<Response>} - Response
   */
  delete(hpId: ID, hpViewId: ID, data: any, entryPoint: string): Observable<void> {
    return this.httpDelete<any>(
      getGTOEndpoint(`${hpId}/#-views/${hpViewId}?entryPoint=${entryPoint}`, entryPoint),
      data,
      identity(),
    );
  }
}
